import React, {useState, useEffect} from 'react';
import {Modal, Button, Form, InputGroup, Spinner, Alert} from 'react-bootstrap';

interface AddEmailAccountModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (account: {
    account: string;
    password: string;
    name: string;
    quota: number;
  }) => Promise<void>;
  domainName: string;
}

const AddEmailAccountModal = ({
  show,
  onHide,
  onAdd,
  domainName,
}: AddEmailAccountModalProps): JSX.Element => {

  const [formData, setFormData] = useState({
    compteLocal: '',
    nom: '',
    quota: '',
    motDePasse: '',
    confirmMotDePasse: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  // Reset form on modal open
  useEffect(() => {
    if (show) {
      setFormData({
        compteLocal: '',
        nom: '',
        quota: '',
        motDePasse: '',
        confirmMotDePasse: '',
      });
      setPasswordError('');
      setShowPassword(false);
      setShowConfirmPassword(false);
      setValidated(false);
      setIsLoading(false);
      setApiError('');
    }
  }, [show]);

  const handleChange = (field: string, value: string) => {
    setFormData({
      ...formData,
      [field]: value,
    });

    // Reset password errors on change
    if (field === 'motDePasse' || field === 'confirmMotDePasse') {
      setPasswordError('');
    }

    // Reset API error on any change
    if (apiError) {
      setApiError('');
    }
  };

  const validatePasswords = (): boolean => {
    if (formData.motDePasse.length < 12) {
      setPasswordError('Le mot de passe doit contenir au moins 12 caractères');
      return false;
    }

    if (formData.motDePasse !== formData.confirmMotDePasse) {
      setPasswordError('Les mots de passe ne correspondent pas');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const form = e.currentTarget as HTMLFormElement;

    // Activer l'affichage des erreurs
    setValidated(true);

    // Vérifier la validité HTML5
    if (!form.checkValidity()) {
      e.stopPropagation();
      return;
    }

    if (!validatePasswords()) {
      return;
    }

    setIsLoading(true);
    setApiError('');

    try {
      // Conversion du quota en octets (Mo -> octets)
      const quotaInBytes = Number(formData.quota) * 1000000;

      await onAdd({
        account: formData.compteLocal,
        password: formData.motDePasse,
        name: formData.nom,
        quota: quotaInBytes,
      });

      // Fermeture du modal après succès
      onHide();
    } catch (error: any) {
      console.error('Erreur lors de la création du compte:', error);

      // Gestion des messages d'erreur
      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else if (error.response?.status === 409) {
        setApiError('Ce compte email existe déjà');
      } else if (error.response?.status === 400) {
        setApiError('Données invalides. Veuillez vérifier les informations saisies');
      } else {
        setApiError('Erreur lors de la création du compte. Veuillez réessayer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-plus me-2 text-primary"></i>
          Ajouter un compte de messagerie
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {apiError && (
          <Alert variant="danger" dismissible onClose={() => setApiError('')}>
            <i className="fa-solid fa-circle-exclamation me-2"></i>
            {apiError}
          </Alert>
        )}

        <Form noValidate validated={validated} id="addEmailForm" onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="required">Compte</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type="text"
                value={formData.compteLocal}
                onChange={(e) => handleChange('compteLocal', e.target.value)}
                placeholder="contact"
                required
                pattern="[a-zA-Z0-9._-]+"
                isInvalid={validated && !formData.compteLocal}
                disabled={isLoading}
              />
              <InputGroup.Text className="bg-light">
                <strong>@{domainName.toLowerCase()}</strong>
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                Veuillez saisir la partie locale de l'adresse email.
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              L'adresse complète sera : <strong>{formData.compteLocal || 'contact'}@{domainName.toLowerCase()}</strong>
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Nom</Form.Label>
            <Form.Control
              type="text"
              value={formData.nom}
              onChange={(e) => handleChange('nom', e.target.value)}
              placeholder="Ex: Contact, Support, Admin..."
              required
              isInvalid={validated && !formData.nom}
              disabled={isLoading}
            />
            <Form.Control.Feedback type="invalid">
              Veuillez saisir un nom pour le compte.
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Nom d'affichage du compte
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Quota</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type="number"
                value={formData.quota}
                onChange={(e) => handleChange('quota', e.target.value)}
                placeholder="500"
                required
                min="1"
                step="1"
                isInvalid={validated && (!formData.quota || Number(formData.quota) < 1)}
                disabled={isLoading}
              />
              <InputGroup.Text className="bg-light">
                <strong>Mo</strong>
              </InputGroup.Text>
              <Form.Control.Feedback type="invalid">
                Veuillez saisir un quota valide (minimum 1 Mo).
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Espace de stockage alloué au compte (en mégaoctets)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Mot de passe</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={formData.motDePasse}
                onChange={(e) => handleChange('motDePasse', e.target.value)}
                placeholder="Minimum 12 caractères"
                required
                minLength={12}
                isInvalid={(validated && !formData.motDePasse) || (!!passwordError && passwordError.includes('12 caractères'))}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                title={showPassword ? 'Masquer' : 'Afficher'}
                type="button"
                disabled={isLoading}
              >
                <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {passwordError && passwordError.includes('12 caractères')
                  ? passwordError
                  : 'Veuillez saisir un mot de passe (minimum 12 caractères).'}
              </Form.Control.Feedback>
            </InputGroup>
            <Form.Text className="text-muted">
              Le mot de passe doit contenir au moins 12 caractères
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Confirmer le mot de passe</Form.Label>
            <InputGroup hasValidation>
              <Form.Control
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmMotDePasse}
                onChange={(e) => handleChange('confirmMotDePasse', e.target.value)}
                placeholder="Confirmer le mot de passe"
                required
                minLength={12}
                isInvalid={(validated && !formData.confirmMotDePasse) || (!!passwordError && passwordError.includes('correspondent pas'))}
                disabled={isLoading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                title={showConfirmPassword ? 'Masquer' : 'Afficher'}
                type="button"
                disabled={isLoading}
              >
                <i className={`fa-solid ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
              </Button>
              <Form.Control.Feedback type="invalid">
                {passwordError && passwordError.includes('correspondent pas')
                  ? passwordError
                  : 'Veuillez confirmer le mot de passe.'}
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>

        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          <i className="fa-solid fa-xmark me-2"></i>
          Annuler
        </Button>
        <Button variant="primary" type="submit" form="addEmailForm" disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Création en cours...
            </>
          ) : (
            <>
              <i className="fa-solid fa-plus me-2"></i>
              Créer le compte
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddEmailAccountModal;
